// "Test my mic" on the check-in, ported from ottos-consult src/ConsultPage.jsx
// (the approved Sept 13 meter): ten bars that light with the voice. The stream
// it opens is held (micPermission.ts) and handed to the call, so the owner is
// asked once. Closing the meter never stops the mic.
import { useCallback, useEffect, useRef, useState } from "react";
import { audioContextCtor } from "../call/audio/audioPrime";
import { requestMicrophone, type MicProblem } from "../call/audio/micPermission";
import type { ConsultStrings } from "../strings";

const BAR_COUNT = 10;

// Voice level from the time-domain signal in decibels mapped onto 0..1, so ordinary
// speech lights most of the meter and a quiet room lights almost none of it.
export function levelFromTimeDomain(data: Uint8Array): number {
  let sum = 0;
  for (let index = 0; index < data.length; index += 1) {
    const value = ((data[index] ?? 128) - 128) / 128;
    sum += value * value;
  }
  const rms = Math.sqrt(sum / Math.max(1, data.length));
  return Math.min(1, Math.max(0, (20 * Math.log10(rms + 1e-6) + 54) / 40));
}

export function micProblemText(s: ConsultStrings, problem: MicProblem): string {
  switch (problem) {
    case "unavailable":
      return s.mic.unavailable;
    case "blocked_by_system":
      return s.mic.blockedBySystem;
    case "no_device":
      return s.mic.noDevice;
    case "in_use":
      return s.mic.inUse;
    default:
      return s.mic.denied;
  }
}

type TestState = "idle" | "requesting" | "live" | "failed";

export function MicCheck({ s, onResult }: { s: ConsultStrings; onResult: (granted: boolean) => void }) {
  const [state, setState] = useState<TestState>("idle");
  const [problem, setProblem] = useState<MicProblem | null>(null);
  const [heard, setHeard] = useState(false);
  const barsRef = useRef<(HTMLSpanElement | null)[]>([]);
  const meterRef = useRef<{
    context: AudioContext;
    source: MediaStreamAudioSourceNode;
    analyser: AnalyserNode;
    frame: number;
    level: number;
  } | null>(null);
  const heardRef = useRef(false);

  const stopMeter = useCallback(() => {
    const meter = meterRef.current;
    meterRef.current = null;
    if (!meter) return;
    cancelAnimationFrame(meter.frame);
    meter.source.disconnect();
    meter.analyser.disconnect();
    void meter.context.close().catch(() => undefined);
    barsRef.current.forEach((bar) => bar?.classList.remove("is-live"));
  }, []);

  useEffect(() => stopMeter, [stopMeter]);

  // The AudioContext is created inside the tap so iOS Safari lets it run.
  const test = async () => {
    if (state === "requesting" || meterRef.current) return;
    const Ctor = audioContextCtor();
    let context: AudioContext | null = null;
    try {
      context = Ctor ? new Ctor() : null;
      void context?.resume().catch(() => undefined);
    } catch {
      context = null;
    }
    setState("requesting");
    setProblem(null);
    const mic = await requestMicrophone();
    if (!mic.granted) {
      void context?.close().catch(() => undefined);
      setState("failed");
      setProblem(mic.problem);
      onResult(false);
      return;
    }
    setState("live");
    onResult(true);
    if (!context) return;
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.2;
    const source = context.createMediaStreamSource(mic.stream);
    source.connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    const meter = { context, source, analyser, frame: 0, level: 0 };
    meterRef.current = meter;
    const tick = () => {
      if (meterRef.current !== meter) return;
      analyser.getByteTimeDomainData(data);
      const target = levelFromTimeDomain(data);
      meter.level = target > meter.level ? target : meter.level * 0.86 + target * 0.14;
      const lit = Math.round(meter.level * BAR_COUNT);
      barsRef.current.forEach((bar, index) => bar?.classList.toggle("is-live", index < lit));
      if (meter.level > 0.5 && !heardRef.current) {
        heardRef.current = true;
        setHeard(true);
      }
      meter.frame = requestAnimationFrame(tick);
    };
    tick();
  };

  const live = state === "live";
  const status =
    state === "requesting"
      ? { tone: "", text: s.checkin.micAllowPrompt }
      : live
        ? heard
          ? { tone: "is-good", text: s.checkin.micGood }
          : { tone: "", text: s.checkin.micListening }
        : problem
          ? { tone: "is-bad", text: micProblemText(s, problem) }
          : null;

  return (
    <div>
      <div className={`oo-mic-bars${live ? " is-on" : ""}`} aria-hidden="true">
        {Array.from({ length: BAR_COUNT }, (_, index) => (
          <span
            key={index}
            ref={(node) => {
              barsRef.current[index] = node;
            }}
            style={{ ["--bar-scale" as string]: String(0.28 + (0.72 * (index + 1)) / BAR_COUNT) }}
          />
        ))}
      </div>
      <p className="oo-quick-label">{s.checkin.micLabel}</p>
      <p className="oo-quick-copy">{s.checkin.micCopy}</p>
      <div className="oo-mic-test">
        {!live && problem !== "unavailable" && (
          <button
            type="button"
            className="oo-mic-test__button"
            onClick={() => void test()}
            disabled={state === "requesting"}
          >
            {state === "requesting"
              ? s.checkin.micWaiting
              : state === "failed"
                ? s.checkin.micTryAgain
                : s.checkin.micTest}
          </button>
        )}
        {status && (
          <p className={`oo-mic-test__status ${status.tone}`} role="status" aria-live="polite">
            {status.text}
          </p>
        )}
      </div>
    </div>
  );
}
